import { useEffect, useRef, useState } from "react";

const DURATION = 400; // ms
const FADE_DURATION = 200; // ms para textos no numéricos

/** Extrae prefijo no-numérico, número, y sufijo */
const parse = (str: string): { prefix: string; num: number; decimals: number; suffix: string } | null => {
  const match = str.replace(/[−–]/g, "-").replace(/,/g, "").match(/^([^0-9\-]*)([-]?\d+\.?\d*)(.*)/);
  if (!match) return null;
  const raw = match[2];
  const dotIdx = raw.indexOf(".");
  return { prefix: match[1], num: parseFloat(raw), decimals: dotIdx >= 0 ? raw.length - dotIdx - 1 : 0, suffix: match[3] };
};

const format = (n: number, decimals: number, prefix: string, suffix: string): string => {
  const parts = Math.abs(n).toFixed(decimals).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${n < 0 ? "-" : ""}${prefix}${parts.join(".")}${suffix}`;
};

interface Props {
  value: string;
  className?: string;
}

const AnimatedValue: React.FC<Props> = ({ value, className }) => {
  const [display, setDisplay] = useState(value);
  const [fade, setFade] = useState(true);
  const prevRef = useRef(value);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = value;
    if (prev === value) { setDisplay(value); return; }

    const from = parse(prev);
    const to = parse(value);

    // Si alguno no es numérico, fade out → cambiar → fade in
    if (!from || !to) {
      setFade(false);
      const t = setTimeout(() => { setDisplay(value); setFade(true); }, FADE_DURATION);
      return () => clearTimeout(t);
    }

    cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - start) / DURATION, 1);
      // Ease-out cubic
      const ease = 1 - Math.pow(1 - t, 3);
      const current = from.num + (to.num - from.num) * ease;
      setDisplay(format(current, to.decimals, to.prefix, to.suffix));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);

  return <span className={className} style={{ transition: `opacity ${FADE_DURATION}ms ease`, opacity: fade ? 1 : 0 }}>{display}</span>;
};

export default AnimatedValue;
